import { z } from 'zod';
import { COMMAND_STATUSES, flagSchema } from './envelope.js';

/**
 * What `POST /commands` answers for each command of a batch (plan §9.3, §9.5; D-024).
 *
 * **Stored** answers are final. Sending the command again returns the same answer (for
 * `held`, or the result a manager's decision stored later):
 *   - `applied`: done;
 *   - `applied_with_flags`: done, with at least one flag for a person to review;
 *   - `rejected` (with `reason`): will never apply. Only for malformed or unauthorised
 *     **non-physical** commands; physical facts are applied with flags instead;
 *   - `held` (with `reason`, `hold_id`): the command **needs a person**. The server keeps
 *     it verbatim for a manager to resolve in the Admin (apply it, or discard it with a
 *     reason), and the station's outbox moves on, so one bad command can never wedge a
 *     device. Physical facts in `held` are not lost: resolving applies them.
 *
 * **Transient** answers are never stored. The device keeps the command in its outbox and
 * sends it again later, unchanged — each of them **clears by itself**:
 *   - `retry_later` (with `reason`): not applied this time (a server error, or a command
 *     type or schema version this server doesn't know yet);
 *   - `upgrade_required` (with `reason`): the station's app or schema version is older than
 *     the server accepts. Update the station first;
 *   - `not_processed`: an earlier command in the same batch got a transient answer, so this
 *     one was not looked at.
 *
 * The server applies a batch in order and **stops at the first transient answer**: every
 * later command is `not_processed`. So the outbox stays FIFO: a command is never applied
 * while an earlier command from the same device is still waiting to be retried.
 */
export const TRANSIENT_COMMAND_STATUSES = [
  'retry_later',
  'upgrade_required',
  'not_processed',
] as const;
/** One of {@link TRANSIENT_COMMAND_STATUSES}. */
export type TransientCommandStatus = (typeof TRANSIENT_COMMAND_STATUSES)[number];

/**
 * Every final answer: the statuses in `command_log`, plus `held` (kept for a person in
 * `command_hold`). A station removes a command from its outbox only after one of these.
 */
export const STORED_COMMAND_STATUSES = [...COMMAND_STATUSES, 'held'] as const;
/** One of {@link STORED_COMMAND_STATUSES}. */
export type StoredCommandStatus = (typeof STORED_COMMAND_STATUSES)[number];

/** True for `applied`, `applied_with_flags`, `rejected` and `held`: final answers. */
export function isStoredStatus(status: string): status is StoredCommandStatus {
  return (STORED_COMMAND_STATUSES as readonly string[]).includes(status);
}

/** A final answer, as stored in `command_log.result`. */
export const storedCommandResponseSchema = z.object({
  command_id: z.uuid(),
  status: z.enum(COMMAND_STATUSES),
  flags: z.array(flagSchema),
  reason: z.string().min(1).optional(),
  /** Handler output (ids, numbers the device needs). */
  data: z.record(z.string(), z.unknown()).optional(),
  /** Human-readable detail. */
  message: z.string().optional(),
});
/** A stored (final) command answer. */
export type StoredCommandResponse = z.infer<typeof storedCommandResponseSchema>;

/**
 * Why a command must be sent again later. Each of these **clears by itself** (a deploy, a
 * station update, a passing fault), so resending it unchanged is right (D-024).
 */
export const RETRY_REASONS = [
  /** This server does not know the command type (the station is newer). */
  'unknown_command_type',
  /** The schema version is newer than this server accepts. */
  'unsupported_schema_version',
  /** Unexpected server failure; nothing was written. */
  'internal_error',
] as const;
/** One of {@link RETRY_REASONS}. */
export type RetryReason = (typeof RETRY_REASONS)[number];

/** Transient: not applied, nothing stored; send the command again later. */
export const retryLaterResponseSchema = z.object({
  command_id: z.string().nullable(),
  status: z.literal('retry_later'),
  reason: z.enum(RETRY_REASONS),
  message: z.string().optional(),
});
/** A `retry_later` answer. */
export type RetryLaterResponse = z.infer<typeof retryLaterResponseSchema>;

/**
 * Why a command is `held` for a person (D-024). Resending never clears these, so the server
 * keeps the command and the station moves on.
 */
export const HOLD_REASONS = [
  /** The envelope failed validation (no usable command id, device, staff, …). */
  'invalid_envelope',
  /**
   * `device_id` is neither the authenticated device nor an earlier device of the same
   * station (re-pairing), or the stored command belongs to another device.
   */
  'device_mismatch',
  /** The command id is already used by another account. */
  'command_id_conflict',
  /** The command came from a revoked device (its queue uploaded after re-pairing). */
  'device_revoked',
  /** A physical fact's handler could not apply it (physical facts are never rejected). */
  'physical_rejected',
] as const;
/** One of {@link HOLD_REASONS}. */
export type HoldReason = (typeof HOLD_REASONS)[number];

/** Stored: kept for a manager to resolve in the Admin; the station moves on. */
export const heldCommandResponseSchema = z.object({
  /** The command's id, or null when the envelope had none. */
  command_id: z.string().nullable(),
  status: z.literal('held'),
  reason: z.enum(HOLD_REASONS),
  /** The hold a manager resolves (`/held-commands/:id`). */
  hold_id: z.uuid(),
  message: z.string().optional(),
});
/** A `held` answer. */
export type HeldCommandResponse = z.infer<typeof heldCommandResponseSchema>;

/** What is too old when a station must update. */
export const UPGRADE_REASONS = [
  /** The command's schema version is below the oldest this server accepts. */
  'schema_version',
  /** The station app is below the account's minimum version for its platform. */
  'app_version',
] as const;
/** One of {@link UPGRADE_REASONS}. */
export type UpgradeReason = (typeof UPGRADE_REASONS)[number];

/** Transient: the station must update before this command can be applied. */
export const upgradeRequiredResponseSchema = z.object({
  command_id: z.string().nullable(),
  status: z.literal('upgrade_required'),
  reason: z.enum(UPGRADE_REASONS),
  /** With `schema_version`: the oldest and the newest schema versions accepted. */
  min_schema_version: z.int().positive().optional(),
  current_schema_version: z.int().positive().optional(),
  /** With `app_version`: the lowest app version accepted for the station's platform. */
  min_app_version: z.string().optional(),
});
/** An `upgrade_required` answer. */
export type UpgradeRequiredResponse = z.infer<typeof upgradeRequiredResponseSchema>;

/**
 * The station's update gate (plan §9.5, lesson 20), in the **same shape** wherever the
 * server says the station app must update, so a station raises its gate from any of them:
 *   - `426` problem+json from `POST /devices/register` and `POST /devices/token`
 *     (`code: 'upgrade_required'`): these members at the top level of the problem;
 *   - `GET /devices/config`: `upgrade`, present only while the app is too old;
 *   - `POST /commands`: `upgrade`, present only when the batch stopped for an update.
 * `upgradeNoticeFrom()` (@enp/domain/api) reads it from any of those bodies.
 */
export const upgradeNoticeSchema = z.object({
  reason: z.enum(UPGRADE_REASONS),
  /**
   * With `app_version`: the lowest app version accepted for the station's platform. Null
   * with `schema_version` (the server can't tell which app release carries that schema).
   */
  min_app_version: z.string().nullable(),
});
/** The update gate the server asks a station to raise. */
export type UpgradeNotice = z.infer<typeof upgradeNoticeSchema>;

/** Transient: skipped because an earlier command in the batch got a transient answer. */
export const notProcessedResponseSchema = z.object({
  command_id: z.string().nullable(),
  status: z.literal('not_processed'),
});
/** A `not_processed` answer. */
export type NotProcessedResponse = z.infer<typeof notProcessedResponseSchema>;

/** Any per-command answer of `POST /commands`. */
export const commandResponseSchema = z.union([
  storedCommandResponseSchema,
  heldCommandResponseSchema,
  retryLaterResponseSchema,
  upgradeRequiredResponseSchema,
  notProcessedResponseSchema,
]);
/** A per-command answer. */
export type CommandResponse = z.infer<typeof commandResponseSchema>;

/** Maximum commands per `POST /commands` request. */
export const MAX_COMMAND_BATCH = 100;

/** Body of `POST /commands`. Each command is validated one by one, not here. */
export const commandBatchRequestSchema = z.object({
  commands: z.array(z.unknown()).min(1).max(MAX_COMMAND_BATCH),
});

/** Response of `POST /commands`: one answer per command, in order. */
export const commandBatchResponseSchema = z.object({
  results: z.array(commandResponseSchema),
  /** True when some command got `upgrade_required`: update the station, then resend. */
  upgrade_required: z.boolean(),
  /** Present exactly when `upgrade_required` is true: the gate to raise. */
  upgrade: upgradeNoticeSchema.optional(),
});
/** Response of `POST /commands`. */
export type CommandBatchResponse = z.infer<typeof commandBatchResponseSchema>;

/**
 * Whether a station may remove an uploaded batch from its outbox: only when the server
 * answered every command and every answer is stored (final — `held` included: the server
 * keeps a held command for a manager). Any transient answer (`retry_later`,
 * `not_processed`, `upgrade_required`) or a missing answer means the whole batch stays
 * queued and is sent again, in order. Stored answers are idempotent, so re-sending
 * already-applied commands is harmless.
 */
export function canCompleteBatch(sent: number, response: CommandBatchResponse): boolean {
  return (
    !response.upgrade_required &&
    response.results.length === sent &&
    response.results.every((r) => isStoredStatus(r.status))
  );
}
